import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, Mail, AlertTriangle, ArrowLeft, PackageCheck } from 'lucide-react';
import { useShop } from '../context/ShopContext';

export const OrderConfirmation: React.FC = () => {
  const { t, locale } = useShop();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Parse URL hash params or search params
    const hash = window.location.hash;
    const queryIdx = hash.indexOf('?');
    const searchString = queryIdx > -1 ? hash.substring(queryIdx) : window.location.search;
    const params = new URLSearchParams(searchString);

    const ref = params.get('ref');
    const token = params.get('token');

    if (!ref || !token) {
      setError(t.order.notFound);
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        const res = await fetch(`/api/order/status?ref=${encodeURIComponent(ref)}&token=${encodeURIComponent(token)}`);
        if (!res.ok) {
          setError(t.order.notFound);
        } else {
          const data = await res.json();
          setOrder(data);
        }
      } catch (err) {
        setError(t.order.notFound);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();

    // Poll once after 4 seconds to catch quick webhook confirmations
    const timer = setTimeout(fetchOrder, 4000);
    return () => clearTimeout(timer);
  }, [t]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-sm text-gray-500">{t.order.loadingOrder}</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center space-y-4">
        <div className="w-14 h-14 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto">
          <AlertTriangle className="w-7 h-7" />
        </div>
        <h2 className="text-lg font-bold text-gray-900">{t.order.notFound}</h2>
        <a
          href="#/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-900 text-white rounded-xl text-xs font-bold hover:bg-pink-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t.order.backToHome}</span>
        </a>
      </div>
    );
  }

  const isEmailSent = order.emailStatus === 'sent' || order.confirmationEmailSent;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 sm:py-16 space-y-8">
      {/* Header Badge */}
      <div className="text-center space-y-3">
        <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto ring-8 ring-emerald-50/50">
          <CheckCircle className="w-8 h-8" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-gray-900">
          {t.order.confirmationTitle}
        </h1>
        <p className="text-sm text-gray-600">
          {t.order.reference} : <span className="font-mono font-bold text-gray-900">{order.orderRef}</span>
        </p>
      </div>

      {/* Honest Email Status Notification Box */}
      <div
        className={`p-4 rounded-2xl border text-xs leading-relaxed flex items-start gap-3 transition-colors ${
          isEmailSent
            ? 'bg-emerald-50/80 border-emerald-200 text-emerald-900'
            : 'bg-amber-50/80 border-amber-200 text-amber-900'
        }`}
      >
        {isEmailSent ? (
          <Mail className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
        ) : (
          <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5 animate-pulse" />
        )}
        <div className="space-y-1">
          <p className="font-bold">
            {isEmailSent
              ? (locale === 'de' ? 'Bestätigungs-E-Mail übermittelt' : 'E-mail de confirmation transmis')
              : (locale === 'de' ? 'E-Mail in Vorbereitung' : 'E-mail en cours d’envoi')}
          </p>
          <p>
            {isEmailSent ? t.order.emailStatusSent : t.order.emailStatusPending}
          </p>
        </div>
      </div>

      {/* Order Summary Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
          <div className="flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-gray-700" />
            <span className="font-bold text-sm text-gray-900">{t.order.itemsOrdered}</span>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-full">
            {order.paymentStatus === 'paid' ? t.order.confirmedBadge : t.order.pendingBadge}
          </span>
        </div>

        {/* Ordered items */}
        <div className="divide-y divide-gray-100">
          {order.items?.map((it: any, idx: number) => (
            <div key={idx} className="py-3 flex justify-between items-center text-xs">
              <div>
                <p className="font-bold text-gray-900">{it.product_name}</p>
                {it.variant_name && <p className="text-gray-500 text-[11px]">{it.variant_name}</p>}
                <p className="text-gray-500 mt-0.5 font-mono">
                  {it.quantity} × {Number(it.unit_price).toFixed(2)} {order.currency}
                </p>
              </div>
              <span className="font-mono font-bold text-gray-900">
                {Number(it.total_price).toFixed(2)} {order.currency}
              </span>
            </div>
          ))}
        </div>

        {/* Financial Breakdown */}
        <div className="border-t border-gray-100 pt-4 space-y-1.5 text-xs text-gray-600">
          <div className="flex justify-between">
            <span>{t.cart.subtotal}</span>
            <span className="font-mono font-medium">{Number(order.subtotal).toFixed(2)} {order.currency}</span>
          </div>

          {Number(order.discountAmount) > 0 && (
            <div className="flex justify-between text-pink-600 font-medium">
              <span>{t.cart.discount}</span>
              <span className="font-mono">-{Number(order.discountAmount).toFixed(2)} {order.currency}</span>
            </div>
          )}

          <div className="flex justify-between">
            <span>{t.cart.shipping}</span>
            <span className="font-mono font-medium">
              {Number(order.shippingAmount) === 0 ? (
                <span className="text-emerald-600 font-semibold">{t.cart.freeShipping}</span>
              ) : (
                `${Number(order.shippingAmount).toFixed(2)} ${order.currency}`
              )}
            </span>
          </div>

          <div className="flex justify-between text-sm font-bold text-gray-900 pt-3 border-t border-gray-100">
            <span>{t.cart.total}</span>
            <span className="font-mono text-base">{Number(order.totalAmount).toFixed(2)} {order.currency}</span>
          </div>
        </div>
      </div>

      {/* Discrete Packing Notice */}
      <p className="text-center text-xs text-gray-500">
        {t.order.deliveryNotice}
      </p>

      {/* Action CTA */}
      <div className="text-center pt-2">
        <a
          href="#/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-900 text-white rounded-xl text-xs font-bold hover:bg-pink-600 transition-colors shadow-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t.order.backToHome}</span>
        </a>
      </div>
    </div>
  );
};
